import Config from '@/config'
import { exchangeLoginCodeForToken } from '@/services/auth'
import { useAuthStore } from '@/store'
import { AuthLoginState } from '@/store/auth/AuthenticationStore'
import { Header } from '@/ui/core/header'
import { ExternalLink, RouterLink } from '@/ui/core/link'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router, useLocalSearchParams } from 'expo-router'
import { useRef } from 'react'
import { Platform, StyleSheet, type ViewStyle } from 'react-native'
import { OtpInput } from 'react-native-otp-entry'
import type { OtpInputRef } from 'react-native-otp-entry'
import Toast from 'react-native-toast-message'

const InvalidOtpCodeText =
	'Please ensure you have entered the correct code sent to your email and try again.'

const OtpStyles = StyleSheet.create({
	containerStyle: {
		maxWidth: 320,
	},
	pinCode: {
		color: '#106A5C',
	},
	filledInCode: {
		borderColor: '#106A5C',
	},
	pinCodeContainer: {
		borderRadius: 0,
		borderColor: '#95CFC4',
		borderWidth: 2,
		maxWidth: 40,
	},
})

const SubmittedLoginForm = () => {
	const isMobileDevice = Platform.OS === 'android' || Platform.OS === 'ios'
	const { setLoginState } = useAuthStore()
	const otpRef = useRef<OtpInputRef>()

	const { redirectTo } = useLocalSearchParams<{ redirectTo?: string }>()

	const resetSubmission = () => {
		setLoginState(AuthLoginState.PENDING)
	}

	const submitOtpCode = async (code: string) => {
		const onSuccess = () => {
			redirectTo ? router.navigate(redirectTo) : router.navigate('/index')
		}

		const onFailure = () => {
			otpRef?.current?.clear()

			Toast.show({
				type: 'error',
				text2: InvalidOtpCodeText,
			})
		}

		await exchangeLoginCodeForToken(code, onSuccess, onFailure)
	}

	return (
		<VStack gap={16} className={'lg:px-6 flex-1 pt-0 px-[15%] h-full'}>
			<VStack gap={64} fullHeight>
				<VStack gap={24} fullHeight className="justify-center">
					<VStack className="grow justify-center" gap={24}>
						<Header className="max-w-72 pb-6" variant="h1">
							Check your email!
						</Header>
						<Text className="lg:font-apercu-light">
							Check your email and enter the code provided.
						</Text>
						<OtpInput
							//@ts-ignore
							ref={otpRef}
							numberOfDigits={6}
							focusColor={'#106A5C'}
							onFilled={submitOtpCode}
							theme={{
								containerStyle: OtpStyles.containerStyle as ViewStyle,
								pinCodeContainerStyle: OtpStyles.pinCodeContainer as ViewStyle,
								filledPinCodeContainerStyle: OtpStyles.filledInCode as ViewStyle,
								pinCodeTextStyle: OtpStyles.pinCode as ViewStyle,
							}}
						/>
					</VStack>
					<Text className="lg:font-apercu-light text-xs text-balance sm:text-pretty mb-12">
						Didn't get an email?{'  '}
						<RouterLink
							className="text-xs"
							onPress={resetSubmission}
							//@ts-ignore
							href={{
								pathname: '/login',
								params: {
									redirectTo,
								},
							}}
						>
							Go back
						</RouterLink>{' '}
						and make sure to use the same email you signed up with or head over to{' '}
						{isMobileDevice && (
							<RouterLink className="text-xs" onPress={resetSubmission} href="/join">
								app.carebywally.com/join
							</RouterLink>
						)}
						{!isMobileDevice && (
							<ExternalLink
								className="text-xs"
								onPress={resetSubmission}
								href={Config.JOIN_URL}
							>
								app.carebywally.com/join
							</ExternalLink>
						)}{' '}
						to sign up.
					</Text>
				</VStack>
			</VStack>
		</VStack>
	)
}

export { SubmittedLoginForm }
